import { Injectable, signal, computed } from '@angular/core';
import type { MerchantDetails } from '../models';

/**
 * MerchantStore - In-memory merchant state management
 *
 * Security:
 * - Merchant data (including balance) stored in memory only
 * - Never persisted to localStorage
 * - Automatically cleared on logout
 * - Auto-refreshes every 5 minutes to keep data current
 * - Smart caching to reduce API calls (3-minute cache)
 */
@Injectable({
  providedIn: 'root',
})
export class MerchantStore {
  // Private signals for internal state
  private readonly merchantSignal = signal<MerchantDetails | null>(null);
  private readonly accountIdSignal = signal<string | null>(null);
  private readonly loadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);
  private readonly lastFetchTimeSignal = signal<number | null>(null);

  // Public readonly signals
  readonly merchant = this.merchantSignal.asReadonly();
  readonly accountId = this.accountIdSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly lastFetchTime = this.lastFetchTimeSignal.asReadonly();

  // Computed signals for UI (with fallback values)
  readonly balance = computed(() => this.merchant()?.balance?.available_balance ?? 0);
  readonly gozemMoneyBalance = computed(() => this.merchant()?.balance?.gozem_money_balance ?? 0);
  readonly overdraftTotal = computed(() => this.merchant()?.balance?.overdraft_total ?? 0);
  readonly currency = computed(() => this.merchant()?.country?.currencysign ?? 'F');
  readonly currencyCode = computed(() => this.merchant()?.country?.currencycode ?? 'XOF');
  readonly displayDecimal = computed(() => this.merchant()?.country?.display_decimal ?? 2);
  readonly displayName = computed(() => this.merchant()?.name ?? 'Merchant');
  /** E.164-prefixed dial code derived from the merchant's country (e.g. '+228'). */
  readonly dialCode = computed(() => {
    const code = this.merchant()?.country?.countryphonecode;
    if (!code) return '+228';
    // API may return the code already prefixed with '+' (e.g. '+228') or bare (e.g. '228').
    // Normalise to always have exactly one '+' so GzSimplePhoneInput shows '+228', not '++228'.
    return code.startsWith('+') ? code : `+${code}`;
  });
  /** Expected local phone number length for the merchant's country (e.g. 8 for Togo). */
  readonly phoneNumberLength = computed(() => this.merchant()?.country?.phone_number_length ?? 8);
  readonly email = computed(() => this.merchant()?.email ?? '');
  readonly phone = computed(() => this.merchant()?.phone ?? '');
  readonly accountNumber = computed(() => this.merchant()?.account_number ?? '');
  readonly merchantId = computed(() => this.merchant()?._id ?? '');
  readonly hasNotifications = computed(() => this.merchant()?.has_notifications ?? false);
  readonly unreadNotifications = computed(() => this.merchant()?.unread_notifications ?? 0);

  // Cache duration: 3 minutes (shorter for sensitive data like balance)
  private readonly CACHE_DURATION = 3 * 60 * 1000;

  /**
   * Set merchant data in store
   */
  setMerchant(merchant: MerchantDetails, accountId: string): void {
    this.merchantSignal.set(merchant);
    this.accountIdSignal.set(accountId);
    this.lastFetchTimeSignal.set(Date.now());
    this.errorSignal.set(null);
  }

  /**
   * Set loading state
   */
  setLoading(loading: boolean): void {
    this.loadingSignal.set(loading);
  }

  /**
   * Set error state
   */
  setError(error: string | null): void {
    this.errorSignal.set(error);
  }

  /**
   * Clear all merchant data (called on logout)
   */
  clearMerchant(): void {
    this.merchantSignal.set(null);
    this.accountIdSignal.set(null);
    this.lastFetchTimeSignal.set(null);
    this.errorSignal.set(null);
    this.loadingSignal.set(false);
  }

  /**
   * Check if cached data is still valid
   */
  isCacheValid(): boolean {
    const lastFetch = this.lastFetchTime();
    if (!lastFetch) return false;
    return Date.now() - lastFetch < this.CACHE_DURATION;
  }

  /**
   * Check if merchant data exists in store
   */
  hasMerchant(): boolean {
    return this.merchant() !== null;
  }

  /**
   * Format balance with currency for display
   */
  getFormattedBalance(showCurrency = true): string {
    const balance = this.balance();
    const decimal = this.displayDecimal();
    const currency = this.currency();

    const formattedNumber = balance.toLocaleString('fr-FR', {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal,
    });

    return showCurrency ? `${formattedNumber} ${currency}` : formattedNumber;
  }
}
